import { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import styles from './PunchupRadioCardGroup.module.css';

// ── Types ──────────────────────────────────────────────────────────────────

export interface PunchupRadioCardOption {
	value: string;
	label: string;
	/** Optional sub-text shown below the label inside each card. */
	description?: string;
}

export interface PunchupRadioCardGroupProps {
	/** react-hook-form field path (e.g. 'custom_field.only_buyer'). */
	name: string;
	options: PunchupRadioCardOption[];
	label?: string;
	required?: boolean;
}

export interface PunchupRadioCardsProps {
	/** Currently selected value. */
	value: string;
	onChange: (value: string) => void;
	options: PunchupRadioCardOption[];
	label?: string;
}

// ── Shared render (not exported) ───────────────────────────────────────────

const RadioCardList: FC<{
	options: PunchupRadioCardOption[];
	selected: string | undefined;
	onSelect: (value: string) => void;
	label?: string;
	required?: boolean;
}> = ({ options, selected, onSelect, label, required }) => (
	<div className={styles.root}>
		{label && (
			<span className={styles.groupLabel} data-required={required ? 'true' : undefined}>
				{label}
			</span>
		)}
		<div className={styles.cardList}>
			{options.map((option) => {
				const isSelected = selected === option.value;
				return (
					<button
						key={option.value}
						type="button"
						className={[styles.card, isSelected ? styles.cardSelected : ''].filter(Boolean).join(' ')}
						onClick={() => onSelect(option.value)}
					>
						<span className={styles.radioIndicator} aria-hidden="true" />
						<span className={styles.cardContent}>
							<span className={styles.cardLabel}>{option.label}</span>
							{option.description && <span className={styles.cardDescription}>{option.description}</span>}
						</span>
					</button>
				);
			})}
		</div>
	</div>
);

// ── Form-connected version ─────────────────────────────────────────────────

/**
 * PunchupRadioCardGroup — react-hook-form connected.
 *
 * Radio selection rendered as a row of bordered cards, each carrying a label
 * and an optional description line. Requires a `FormProvider` ancestor.
 *
 * For use outside a form context (local state), use `PunchupRadioCards`.
 */
export const PunchupRadioCardGroup: FC<PunchupRadioCardGroupProps> = ({ name, options, label, required }) => {
	const { watch, setValue } = useFormContext();
	const selected = watch(name) as string | undefined;
	return (
		<RadioCardList
			options={options}
			selected={selected}
			onSelect={(v) => setValue(name, v, { shouldValidate: true })}
			label={label}
			required={required}
		/>
	);
};

// ── Standalone controlled version ─────────────────────────────────────────

/**
 * PunchupRadioCards — standalone controlled.
 *
 * Same visual as `PunchupRadioCardGroup` but driven by external `value`/`onChange`
 * props instead of react-hook-form. Use when the selection is local page state
 * rather than a form field (e.g. a mode toggle above a form).
 */
export const PunchupRadioCards: FC<PunchupRadioCardsProps> = ({ value, onChange, options, label }) => (
	<RadioCardList options={options} selected={value} onSelect={onChange} label={label} />
);
